import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { submitLead } from "@/lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const PROPERTY_TYPES = [
    "Apartment",
    "House",
    "Townhouse",
    "Penthouse",
    "Studio",
    "Other",
];

const SERVICE_AREAS = [
    "Werribee",
    "Point Cook",
    "Truganina",
    "Tarneit",
    "Hoppers Crossing",
    "Wyndham Vale",
    "Melton",
    "Caroline Springs",
    "Geelong",
    "Armstrong Creek",
    "Ocean Grove",
    "Torquay",
    "Altona",
    "Williamstown",
    "Footscray",
    "Sunshine",
    "CBD",
    "Southbank",
    "St Kilda",
    "Richmond",
    "South Yarra",
    "Fitzroy",
    "Docklands",
    "Port Melbourne",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        property_type: "",
        suburb: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Please share your name and email.");
            return;
        }
        setLoading(true);
        try {
            await submitLead({
                name: form.name,
                email: form.email,
                phone: form.phone,
                message: form.message,
                suburb: form.suburb,
                property_type: form.property_type
                    ? form.property_type.toLowerCase()
                    : null,
                source: "contact_page",
            });
            toast.success("Enquiry received — we'll be in touch shortly.");
            setForm({
                name: "",
                email: "",
                phone: "",
                property_type: "",
                suburb: "",
                message: "",
            });
        } catch {
            toast.error("Couldn't send — please call 0469 076 071.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-night">
            {/* Hero */}
            <section
                className="pt-36 md:pt-44 pb-12 md:pb-16 text-center"
                data-testid="contact-hero"
            >
                <div className="max-w-[1100px] mx-auto px-6 md:px-12">
                    <div className="eyebrow mb-5">Get In Touch</div>
                    <h1 className="headline text-5xl md:text-7xl lg:text-[88px] text-cream leading-[1.04] mb-6">
                        Let&rsquo;s Discuss<br />
                        <span className="italic text-gold">Your Property</span>
                    </h1>
                    <p className="text-cream/55 font-light text-lg leading-relaxed max-w-2xl mx-auto">
                        Ready to maximise your property&rsquo;s income? Book a free
                        consultation or request a rental estimate. We&rsquo;d love to
                        hear from you.
                    </p>
                </div>
            </section>

            {/* Form + Info */}
            <section className="py-12 md:py-16" data-testid="contact-main">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                    <form
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="lg:col-span-7 bg-surface border border-line-dark p-8 md:p-12"
                    >
                        <div className="eyebrow mb-3">Send us a message</div>
                        <h2 className="headline text-3xl md:text-4xl text-cream mb-10">
                            Book a free consultation.
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <Field label="Full Name *">
                                <input
                                    name="name"
                                    required
                                    value={form.name}
                                    onChange={onChange}
                                    className="input-line"
                                    placeholder="John Smith"
                                    data-testid="contact-form-name"
                                />
                            </Field>
                            <Field label="Email *">
                                <input
                                    name="email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={onChange}
                                    className="input-line"
                                    placeholder="you@example.com"
                                    data-testid="contact-form-email"
                                />
                            </Field>
                            <Field label="Phone">
                                <input
                                    name="phone"
                                    value={form.phone}
                                    onChange={onChange}
                                    className="input-line"
                                    placeholder="04XX XXX XXX"
                                    data-testid="contact-form-phone"
                                />
                            </Field>
                            <Field label="Property Type">
                                <Select
                                    value={form.property_type}
                                    onValueChange={(v) =>
                                        setForm((f) => ({ ...f, property_type: v }))
                                    }
                                >
                                    <SelectTrigger
                                        data-testid="contact-form-type"
                                        className="rounded-none border-0 border-b border-cream/30 bg-transparent focus:ring-0 px-0 h-[42px] text-base text-cream"
                                    >
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-none">
                                        {PROPERTY_TYPES.map((t) => (
                                            <SelectItem key={t} value={t} className="rounded-none">
                                                {t}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Suburb" full>
                                <input
                                    name="suburb"
                                    value={form.suburb}
                                    onChange={onChange}
                                    className="input-line"
                                    placeholder="e.g. Werribee, Geelong, Melton"
                                    data-testid="contact-form-suburb"
                                />
                            </Field>
                            <Field label="Message" full>
                                <textarea
                                    name="message"
                                    value={form.message}
                                    onChange={onChange}
                                    rows={5}
                                    className="input-line"
                                    placeholder="Tell us about your property and goals..."
                                    data-testid="contact-form-message"
                                />
                            </Field>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="contact-form-submit"
                            className="btn-gold mt-10 w-full md:w-auto disabled:opacity-60"
                        >
                            {loading ? "Sending..." : "Send Enquiry"}
                        </button>
                    </form>

                    <aside className="lg:col-span-5 space-y-5">
                        <InfoCard
                            icon={Phone}
                            label="Phone"
                            value={
                                <a href="tel:0469076071" className="text-cream hover:text-gold">
                                    0469 076 071
                                </a>
                            }
                            testid="info-phone"
                        />
                        <InfoCard
                            icon={Mail}
                            label="Email"
                            value={
                                <a
                                    href="mailto:leonce@leosluxury.com.au"
                                    className="text-cream hover:text-gold break-all"
                                >
                                    leonce@leosluxury.com.au
                                </a>
                            }
                            testid="info-email"
                        />
                        <InfoCard
                            icon={MapPin}
                            label="Location"
                            value="Melbourne, Victoria, Australia"
                            testid="info-location"
                        />
                        <InfoCard
                            icon={Clock}
                            label="Hours"
                            value="Mon–Fri 8am–6pm · Sat 9am–3pm"
                            testid="info-hours"
                        />
                    </aside>
                </div>
            </section>

            {/* Service Areas */}
            <section
                className="py-20 md:py-24 bg-surface"
                data-testid="service-areas-section"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <div className="eyebrow mb-4">Service Areas</div>
                        <h2 className="headline text-4xl md:text-5xl text-cream mb-4">
                            Servicing Melbourne&rsquo;s west,<br />
                            <span className="italic text-gold">Geelong &amp; beyond.</span>
                        </h2>
                        <p className="text-cream/55 font-light leading-relaxed">
                            Our focus is the rapidly-growing western suburbs and
                            Geelong corridor — and we extend across greater Melbourne
                            for the right property.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 md:gap-3 justify-center">
                        {SERVICE_AREAS.map((s) => (
                            <span
                                key={s}
                                className="border border-gold/30 text-cream/80 bg-night px-5 py-2 text-[12px] uppercase tracking-[0.18em] hover:bg-gold hover:text-night hover:border-gold transition-colors font-mono"
                                data-testid={`area-${s.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                {s}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Map */}
            <section className="relative" data-testid="map-section">
                <div className="aspect-[16/8] md:aspect-[16/6] bg-[#0A0A0A] relative overflow-hidden">
                    <iframe
                        title="Melbourne Service Area Map"
                        src="https://www.openstreetmap.org/export/embed.html?bbox=144.3%2C-38.3%2C145.2%2C-37.6&layer=mapnik&marker=-37.95,144.65"
                        className="absolute inset-0 w-full h-full"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            </section>
        </div>
    );
}

function Field({ label, children, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="eyebrow text-fog block mb-2">{label}</label>
            {children}
        </div>
    );
}

function InfoCard({ icon: Icon, label, value, testid }) {
    return (
        <div
            data-testid={testid}
            className="border border-line-dark bg-surface p-6 md:p-7 flex gap-5 items-start hover:border-gold/40 transition-colors"
        >
            <div className="shrink-0 w-12 h-12 border border-gold/40 flex items-center justify-center">
                <Icon size={18} strokeWidth={1.2} className="text-gold" />
            </div>
            <div className="flex-1">
                <div className="eyebrow mb-1">{label}</div>
                <div className="font-serif text-xl md:text-2xl text-cream">{value}</div>
            </div>
        </div>
    );
}
